import { animated } from '@react-spring/web';
import styled, { css } from 'styled-components';
import { createShouldForwardStyledProp, modeColor } from '../../styles';
import { Z_INDEX } from '../../styles/layout';
import { Box, Flex, Grid, Stack, type GridProps } from '../Core';

type WrapperProps = { position: 'absolute' | 'fixed' | 'relative'; width?: number; placement: 'left' | 'right' };

export const Wrapper = styled(animated.div)<WrapperProps>`
  height: 100%;
  background: var(--backgroundDrawer);
  backdrop-filter: var(--backdropFilterDrawer);
  z-index: ${Z_INDEX.drawer};
  ${({ width }) => width !== null && `width: ${width}px`};
  min-width: min-content;
  display: flex;
  flex-direction: column;
  align-items: stretch;

  ${({ position, placement }) =>
    position === 'fixed' &&
    `
    position: fixed;
    top: 0;
    bottom: 0;
    ${placement === 'left' ? `left: 0;` : `right: 0`};
  `};

  ${({ position, placement, theme }) =>
    position === 'absolute' &&
    `
    position: absolute;
    top: 0;
    bottom: 0;
    box-shadow: 0 0 40px ${modeColor(theme, theme.colors.black.mute, theme.colors.black.dim)};
    ${placement === 'left' ? `left: 0;` : `right: 0`};
  `};

  ${({ position }) =>
    position === 'relative' &&
    `
    position: relative;
  `};
`;

/** 3-part Drawer header using CSS GRid */
export const DrawerHeader = styled(Grid)`
  display: grid;
  grid-template-columns: ${({ gridTemplateColumns }) => gridTemplateColumns ?? '1fr 1fr 1fr'};
  padding: ${({ p, theme }) => p ?? `0 12px`};
  min-height: 2.5rem;
  border-bottom: solid 1px var(--colors-gray-010);
  flex: 0;
  align-items: center;
`;

/** Simple 2-part Drawer Header using Flexbox */
export const DrawerHeaderFlex = styled(Flex)`
  justify-content: space-between;
  padding: 8px 16px;
  min-height: 3.5rem;
  border-bottom: solid 1px var(--colors-gray-010);
  align-items: center;
`;

export const DrawerContent = (props: GridProps) => {
  return <StyledDrawerContent {...props} p={props.p ?? 'spacing12'} />;
};

const StyledDrawerContent = styled(Grid)``;
StyledDrawerContent.defaultProps = {
  overflow: 'hidden',
  flex: 1,
  pb: '0',
};

export const DrawerFooter = styled(Grid)`
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  border-top: solid 1px var(--colors-gray-010);
`;
DrawerFooter.defaultProps = {
  p: 'spacing16',
  gap: 'spacing16',
};

export const DrawerFooterFlex = styled(Flex)`
  border-top: solid 1px var(--colors-gray-010);
`;
DrawerFooterFlex.defaultProps = {
  p: 'spacing16',
  gap: 'spacing16',
};

const noForwardProps = new Set(['width', '$disableTransition']);

export const DrawerWrapper = styled(Box).withConfig({
  shouldForwardProp: createShouldForwardStyledProp(noForwardProps),
})<{ width?: number; $disableTransition?: boolean }>`
  background: var(--backgroundDrawer);
  transition:
    width 200ms ease,
    max-width 200ms ease,
    margin 200ms ease;
  width: ${({ width }) => (width != null ? `${width}px` : 'auto')};
  max-width: ${({ width }) => (width != null ? `${width}px` : 'none')};
  min-width: 0;
  height: 100%;
  overflow: hidden;
  flex: 0 0 auto;
  contain: ${({ width }) => (width != null ? 'inline-size' : 'none')};

  ${({ $disableTransition }) =>
    $disableTransition &&
    css`
      transition: none;
    `}

  @media print {
    display: none;
  }
`;

const ADDITIONAL_SASH_PADDING = 1;
const SASH_EDGE_PADDING = ADDITIONAL_SASH_PADDING * 2;
const SASH_PILL_HEIGHT = 64;
const SASH_BORDER_RADIUS = 10;

export const DrawerColumn = styled.div<{ $placement: 'left' | 'right'; $width?: number; $disableTransition?: boolean }>`
  position: relative;
  display: flex;
  height: 100%;
  flex: 0 0 auto;
  min-width: 0;
  /* Explicit width so this flex item transitions open. An auto width snaps to the full content size on the first frame. */
  width: ${({ $width }) => ($width != null ? `${$width + SASH_EDGE_PADDING}px` : 'auto')};
  max-width: ${({ $width }) => ($width != null ? `${$width + SASH_EDGE_PADDING}px` : 'none')};
  transition:
    width 200ms ease,
    max-width 200ms ease;

  ${({ $disableTransition }) =>
    $disableTransition &&
    css`
      transition: none;
    `}

  /* Sit in the drawer gap so the sash stays inside this box and avoids ancestor clipping. */
  ${({ $placement, theme }) =>
    $placement === 'right'
      ? css`
          margin-left: calc(${theme.spacingLayout}px * -1);
          padding-left: ${SASH_EDGE_PADDING}px;
        `
      : css`
          margin-right: calc(${theme.spacingLayout}px * -1);
          padding-right: ${SASH_EDGE_PADDING}px;
        `}

  @media print {
    display: none;
  }
`;

/** Matches the dockview horizontal sash affordance in Layout/styles.ts. */
export const DrawerResizeHandle = styled.button<{ $placement: 'left' | 'right'; $isActive?: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  ${({ $placement }) => ($placement === 'right' ? 'left: 0;' : 'right: 0;')}
  width: calc(${({ theme }) => theme.spacingLayout}px + ${ADDITIONAL_SASH_PADDING * 4}px);
  z-index: 10;
  border: none;
  padding: 0;
  background: transparent;
  cursor: ew-resize;
  touch-action: none;

  &::before {
    content: '';
    position: absolute;
    display: block;
    background-color: var(--colors-gray-040);
    inset: 0 ${ADDITIONAL_SASH_PADDING}px;
    opacity: 0;
    transition: opacity ease 200ms;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    height: ${SASH_PILL_HEIGHT}px;
    inset: ${ADDITIONAL_SASH_PADDING}px 0;
    top: 50%;
    transform: translateY(-50%);
    background-color: var(--colors-gray-100);
    border: 1px solid var(--colors-gray-040);
    border-radius: ${SASH_BORDER_RADIUS}px;
    opacity: 0;
    transition: opacity ease 200ms;
    pointer-events: none;
  }

  &:hover::before,
  &:hover::after {
    opacity: 1;
    transition-delay: 100ms;
  }

  ${({ $isActive }) =>
    $isActive &&
    css`
      &::before,
      &::after {
        opacity: 1;
      }
    `}
`;

export const DrawerContentWrapper = styled(Stack).withConfig({
  shouldForwardProp: createShouldForwardStyledProp(noForwardProps),
})<{ width?: number }>`
  position: relative;
  width: ${({ width }) => (width != null ? `${width}px` : 'auto')};
  height: 100%;
  overflow: hidden;
`;
